import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { AutocompleteMulti } from "components/AutocompleteMulti/AutocompleteMulti"
import { StyledButton } from "components/Button/StyledButton"
import { CriteriaCatalog, ExaminationConfig, Norm } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { SyntheticEvent, useEffect, useState } from "react"
import { CriteriaCatalogContent } from "./CriteriaCatalogContent"

type AddCriteriaCatalogProps = {
  open: boolean
  onClose: () => void
  formState: CriteriaCatalog
  onSubmit: (arg: CriteriaCatalog) => void
}

type ValidationErrors = {
  name?: string
}

export const AddCriteriaCatalog = (props: AddCriteriaCatalogProps) => {
  const { open, onClose, formState, onSubmit } = props

  const { norms, examinationConfigs } = useStoreState((state) => state)
  const { fetchNorms, fetchExaminationConfigs } = useStoreActions((actions) => actions)

  const [localFormState, setLocalFormState] = useState<CriteriaCatalog>(formState)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  useEffect(() => {
    const loadNorms = async () => {
      try {
        await fetchNorms({ fresh: false })
      } catch (err) {
        console.error("Error loading norms:", err)
      }
    }
    loadNorms()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const loadExaminationConfigs = async () => {
      try {
        await fetchExaminationConfigs({ fresh: false })
      } catch (err) {
        console.error("Error loading test equipment:", err)
      }
    }
    loadExaminationConfigs()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setLocalFormState(formState)
    setValidationErrors({}) // Clear errors when form state changes
  }, [formState])

  const validateForm = (): ValidationErrors => {
    const errors: ValidationErrors = {}

    // Check required fields
    if (!localFormState.name?.trim()) {
      errors.name = "Name ist erforderlich"
    }

    return errors
  }

  const isFormValid = (): boolean => {
    const errors = validateForm()
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFormChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target
    if (name) {
      setLocalFormState((prevState) => ({ ...prevState, [name]: value }))

      // Clear validation error for this field
      if (validationErrors[name as keyof ValidationErrors]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[name as keyof ValidationErrors]
          return newErrors
        })
      }
    }
  }

  const handleNormChange = (_: SyntheticEvent<Element, Event>, value: Norm[]) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      norm: value.map((item) => item.id),
    }))
  }

  const handleExamChange = (_: SyntheticEvent<Element, Event>, value: ExaminationConfig[]) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      examinationConfig: value.map((item) => item.id),
    }))
  }

  const handleSave = () => {
    if (isFormValid()) {
      onSubmit(localFormState)
    }
  }

  const handleClose = () => {
    setLocalFormState(formState)
    setValidationErrors({})
    onClose()
  }

  const handleUpdateCriteriaCatalog = (updatedCatalog: CriteriaCatalog) => {
    setLocalFormState(updatedCatalog)
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        {localFormState.id !== "NEW"
          ? "Pass/Fail-Kriterium bearbeiten"
          : "Pass/Fail-Kriterium hinzufügen"}
      </DialogTitle>
      <DialogContent sx={{ overflow: "auto", maxHeight: "90vh" }}>
        {hasValidationErrors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              Bitte korrigieren Sie die folgenden Fehler:
            </Typography>
            <Stack spacing={0.5}>
              {Object.entries(validationErrors).map(([field, error]) => (
                <Typography key={field} variant="body2">
                  • {error}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        <TextField
          id="genral"
          size="small"
          name="name"
          label="Name"
          type="text"
          fullWidth
          value={localFormState.name}
          onChange={handleFormChange}
          required
          error={!!validationErrors.name}
          helperText={validationErrors.name}
        />

        <AutocompleteMulti
          options={examinationConfigs.filter((e) => !e.isObsolete)}
          value={examinationConfigs
            .filter((e) => !e.isObsolete)
            .filter((item) => localFormState.examinationConfig.includes(item.id))}
          onChange={handleExamChange}
          label={"Prüfverfahren"}
          required
        />

        <AutocompleteMulti
          options={norms.filter((n) => !n.isDeleted)}
          value={norms
            .filter((n) => !n.isDeleted)
            .filter((item) => localFormState.norm.includes(item.id))}
          onChange={handleNormChange}
          label={"Normen"}
        />

        <CriteriaCatalogContent
          criteriaCatalog={localFormState}
          onUpdateCriteriaCatalog={handleUpdateCriteriaCatalog}
          operatingModes={[]}
        />
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} />
        <StyledButton buttonType="save" onClick={handleSave} disabled={hasValidationErrors} />
      </DialogActions>
    </Dialog>
  )
}
